import { AppMessage } from '@spx-portal/shared-components';
import { getCurrentStationId, INIT_PAGINATION } from '@spx-portal/shared-utils';
import { useRequest } from 'ahooks';
import React from 'react';
import { ProTable } from 'react-pro-components';
import type { ProTableProps } from 'react-pro-components/typings/components/pro-table/types';
import { Button, Form, message, Modal, Tabs } from 'ssc-ui-react';
import { ColumnProps } from 'ssc-ui-react/typings/components/table/types';

import { ReactComponent as EmptyIcon } from '@/assets/svg/empty.svg';
import { $gt } from '@/common/appCtx';
import { usePermissions } from '@/pages/shift/hooks/use-permissions';
import { useCheckGrayStation } from '@/store/gray-station';

import { transformStationShiftListParams } from './adapter';
import * as API from './api';
import TimeSlotModificationHistory from './components/timeslot-modification-history';
import { TabStatus } from './const';
import { useFields } from './fields';
import { useBatchAction } from './hooks/use-batch-action';
import { useShiftModal } from './hooks/use-shift-modal';
import style from './index.module.less';

export default function EnableShiftList() {
  const proTableRef = React.useRef<{fetch?: () => void}>(null);
  const activeTabRef = React.useRef(TabStatus.Enabled);
  const [activeTab, setActiveTab] = React.useState(TabStatus.Enabled);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<string[]>([]);
  const [logVisible, setLogVisible] = React.useState(false);
  const [pagination, setPagination] = React.useState(INIT_PAGINATION);
  const grayStationData = useCheckGrayStation.use.grayStationData();
  const [form] = Form.useForm();
  const { showShiftModal, shiftModalComp } = useShiftModal({ onSuccess: () => proTableRef.current?.fetch?.() });
  const { batchActionComp } = useBatchAction(proTableRef.current?.fetch);
  const permission = usePermissions();

  const { showColumn, queryField } = useFields({ lineClamp: 1, activeTab });

  const { loading, data, run: manualGetStationShiftList } = useRequest(API.getStationShiftList, {
    manual: true,
    onSuccess(data) {
      const {
        total = 0,
        pageno = INIT_PAGINATION.current,
        count = INIT_PAGINATION.pageSize,
      } = data.data || {};
      setPagination({
        ...pagination,
        total,
        current: pageno,
        pageSize: count,
      });
    },
    onError(e) {
      console.error(e);
    },
  });

  const handleClickLogBtn = () => {
    setLogVisible(true);
  };

  const handleExport = async() => {
    const params = transformStationShiftListParams({ model: form.getFieldsValue(), activeTab: activeTabRef.current });
    const query = Object.entries(params).filter(([key, value]) => value !== undefined).map(([key, value]) => `${key}=${value}`).join('&');
    window.open(`/api/wfm/admin/roster_planning/slot/station_slot_list_download?${query}`);
  };

  const showCreateButton = () => {
    if (!permission.hasCreatePermission) {
      return false;
    }
    return grayStationData.station_create_shift_toggle && activeTab === TabStatus.Enabled;
  };

  const Operation = () => {
    return (
      <>
        {showCreateButton() ? (
          <>
            <Button
              type="primary"
              onClick={() => showShiftModal('create',)}
            >
              {$gt('Create')}
            </Button>
            {batchActionComp}
          </>
        ) : null}
        {permission.hasExportPermission ? (
          <Button
            onClick={handleExport}
          >
            {$gt('Export')}
          </Button>
        ) : null}
        <Button onClick={handleClickLogBtn}>{$gt('Log')}</Button>
      </>
    );
  };

  const enableShift = async(slotIdList: string[]) => {
    try {
      await API.enableShift({ slot_id_list: slotIdList });
      message.success($gt('Enabled success! please go to Enabled Shift to check.'));
      setSelectedRowKeys([]);
      proTableRef.current?.fetch?.();
    } catch (e) {
      console.error(e);
    }
  };

  const disableShift = async(row: any) => {
    if (!row.can_disable) {
      AppMessage.error(992600078, $gt('This shift is currently being used by an event and cannot be disabled.'));
      return;
    }
    Modal.confirm({
      title: $gt('Notice'),
      content: $gt('Are you sure you want to disable this shift?'),
      onOk: async() => {
        try {
          await API.disableShift({ slot_id: row.slot_id });
          message.success($gt('Disable Successfully'));
          proTableRef.current?.fetch?.();
        } catch (e) {
          console.error(e);
        }
      },
    });
  };

  const showEditButton = (row: any) => {
    if (!permission.hasEditPermission) {
      return false;
    }
    return activeTab === TabStatus.Enabled && row.create_station_id === getCurrentStationId();
  };
  const showDisableButton = () => {
    return permission.hasEnableDisablePermission && activeTab === TabStatus.Enabled && !grayStationData.hide_slot_disable_page;
  };
  const showEnableButton = () => {
    return permission.hasEnableDisablePermission && activeTab === TabStatus.Pending;
  };

  const actionColumn = () => ({
    actions: (row: any) => {
      return [
        showEditButton(row) ? {
          children: $gt('Edit'),
          onClick: () => {
            showShiftModal('edit', row);
          },
        } : null,
        showDisableButton() ? {
          children: $gt('Disable'),
          onClick: () => disableShift(row),
        } : null,
        showEnableButton() ? {
          children: $gt('Enable'),
          onClick: () => enableShift([row.slot_id]),
        } : null,
      ].filter(Boolean) as { children: string; onClick: () => void }[];
    },
  });

  const fetchData = {
    fetcher: async(info: any) => {
      const { form, pagination: curPagination } = info;
      const { current, pageSize } = curPagination || {};
      manualGetStationShiftList({
        pageno: current || INIT_PAGINATION.current,
        count: pageSize || INIT_PAGINATION.pageSize,
        ...transformStationShiftListParams({ model: form, activeTab: activeTabRef.current }),
      });
    },
    fetchOnMount: true,
    fetchOnFormChange: false,
    fetchOnFormReset: true,
    fetchOnFormSubmit: true,
    fetchOnTableChange: true,
  };

  const handleBatchEnable = () => {
    Modal.confirm({
      title: $gt('Batch Enable Shift'),
      content: $gt('Are you sure you want to enable the shift?'),
      onOk: () => {
        return enableShift(selectedRowKeys);
      },
    });
  };

  const handleTabChange = React.useCallback((key: string) => {
    setActiveTab(key);
    activeTabRef.current = key;
    setPagination(INIT_PAGINATION);
    form.resetFields();
  }, [form]);

  const tableEmptyText = React.useMemo(() => {
    if (activeTab === TabStatus.Enabled && !grayStationData.hide_slot_disable_page) {
      return (
        <div className={style.empty}>
          <EmptyIcon />
          <div>
            <span style={{ marginRight: 8 }}>{$gt('No Data')}</span>
            <Button
              type="link"
              onClick={() => handleTabChange(TabStatus.Pending)}
            >
              {$gt('To Enable Shift')}
            </Button>
          </div>
        </div>
      );
    }
    return undefined;
  }, [activeTab, grayStationData.hide_slot_disable_page, handleTabChange]);

  const proTableProps: ProTableProps = {
    loading,
    searchForm: {
      formProps: {
        form,
      },
      fields: Object.values(queryField),
      footerProps: { showReset: true },
      startCollapseRows: 3,
      columns: 3,
      onReset: () => {
        setSelectedRowKeys([]);
      }
    },
    operation: <Operation />,
    massTool: activeTab === TabStatus.Pending ? {
      text: (
        <>
          {ProTable.formatText('%d Shift(s) Selected', selectedRowKeys.length)}
        </>
      ),
      button: (
        <>
          {permission.hasEnableDisablePermission ? (
            <Button
              disabled={!selectedRowKeys.length}
              onClick={handleBatchEnable}
            >
              {$gt('Batch Enable')}
            </Button>
          ) : null}
        </>
      ),
    } : undefined,
    table: {
      dataSource: data?.data?.list || [],
      columns: Object.values(showColumn) as ColumnProps<any>[],
      actionColumn: {
        width: 150,
        fixed: 'right',
        ...actionColumn(),
      },
      rowKey: 'slot_id',
      rowSelection: activeTab === TabStatus.Pending ? {
        selectedRowKeys,
        onChange: (newKeys: Array<string | number>) => {
          if (newKeys.length > 100) {
            message.error($gt('The number of selected shifts cannot exceed 100'));
            return;
          }
          setSelectedRowKeys(newKeys as string[]);
        },
      } : undefined,
      pagination,
      sticky: true,
      emptyText: tableEmptyText,
      scroll: { x: true },
    },
    fetchData,
  };

  return (
    <>
      <Tabs
        animated
        defaultActiveKey={TabStatus.Enabled}
        activeKey={activeTab}
        style={{ padding: '0 24px', background: '#fff' }}
        onChange={handleTabChange}
      >
        <Tabs.TabPane
          tab={$gt('Enabled Shift')}
          key={TabStatus.Enabled}
        />
        {!grayStationData.hide_slot_disable_page ? (
          <Tabs.TabPane
            tab={$gt('Pending Enabled Shift')}
            key={TabStatus.Pending}
          />
        ) : null}
      </Tabs>
      <ProTable
        key={activeTab}
        ref={proTableRef}
        {...proTableProps}
      />
      <TimeSlotModificationHistory
        visible={logVisible}
        onClose={() => setLogVisible(false)}
      />
      {shiftModalComp}
    </>
  );
}
